var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useContext, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { BLACK_700, tokens } from '../../constants';
import { useBreakpoint } from '../../hooks';
import { StateConfigContext } from '../../providers';
import { getTestId, isLastItem } from '../../utils';
import { Collapsible } from '../collapsible';
import { Typography } from '../typography';
import { Step } from './components/step/Step';
import { getContentContainerStyle, getStepperProps, getStepperStyles, getTypography } from './StepperStyles';
export var Stepper = forwardRef((props, ref) => {
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isLargeScreen = _useBreakpoint.isLargeScreen;
  var stateContext = useContext(StateConfigContext);
  var stepperProps = getStepperProps(props, stateContext);
  var stepperStyles = getStepperStyles(stepperProps, isLargeScreen);
  var titleComponentRef = useRef();
  var titleComponentContainerRef = useRef();
  var _useState = useState(0),
    lastItemConnectorHeight = _useState[0],
    setLastItemConnectorHeight = _useState[1];
  var style = stepperProps.style,
    direction = stepperProps.direction,
    steps = stepperProps.steps,
    config = stepperProps.config,
    size = stepperProps.size,
    isPressableStep = stepperProps.isPressableStep,
    onStepPress = stepperProps.onStepPress,
    _stepperProps$testID = stepperProps.testID,
    testID = _stepperProps$testID === void 0 ? 'Stepper' : _stepperProps$testID,
    showMultiLevelStepper = stepperProps.showMultiLevelStepper,
    restProps = __rest(stepperProps, ["style", "direction", "steps", "config", "size", "isPressableStep", "onStepPress", "testID", "showMultiLevelStepper"]);
  var renderSingleStep = useCallback((step, isLastChild) => {
    var _ref = step || {},
      level = _ref.level,
      state = _ref.state,
      _ref$shouldBlink = _ref.shouldBlink,
      shouldBlink = _ref$shouldBlink === void 0 ? level !== 'L1' && state == 'danger' : _ref$shouldBlink;
    var stepComponent = _jsx(Step, Object.assign({
      step: step
    }, stepperProps, {
      level: step === null || step === void 0 ? void 0 : step.level,
      isLastChild: isLastChild,
      shouldBlink: shouldBlink,
      lastItemConnectorHeight: lastItemConnectorHeight
    }, getTestId(testID, 'singleStep')));
    if (isPressableStep) {
      return _jsx(Pressable, Object.assign({
        onPress: () => onStepPress(step)
      }, {
        children: stepComponent
      }));
    }
    return stepComponent;
  }, [isPressableStep, onStepPress, stepperProps, lastItemConnectorHeight]);
  var renderStepContent = (step, isExpanded) => {
    var _a, _b, _c;
    var heading = step.heading,
      subHeading = step.subHeading,
      headingColor = step.headingColor,
      subHeadingColor = step.subHeadingColor,
      childElement = step.childElement,
      state = step.state,
      level = step.level,
      _step$isHeadingBold = step.isHeadingBold,
      isHeadingBold = _step$isHeadingBold === void 0 ? true : _step$isHeadingBold,
      showSubheadingOnCollapsed = step.showSubheadingOnCollapsed;
    var typographyColor = (_c = (_b = (_a = config === null || config === void 0 ? void 0 : config[size]) === null || _a === void 0 ? void 0 : _a.state) === null || _b === void 0 ? void 0 : _b[state]) === null || _c === void 0 ? void 0 : _c.typographyColor;
    var contentContainerStyle = getContentContainerStyle(level, isLargeScreen, direction);
    var headingTypography = size === 'sm' ? 'body-para-sm-bold' : 'body-para-md-bold';
    if (!heading && !subHeading && !childElement) return null;
    return _jsxs(View, Object.assign({
      style: contentContainerStyle,
      ref: titleComponentContainerRef
    }, getTestId(testID, 'contentContainer'), {
      children: [_jsxs(View, Object.assign({
        ref: titleComponentRef
      }, level == 'L2' && {
        style: {
          paddingLeft: isLargeScreen ? tokens.spacing.SPACING_8 : tokens.spacing.SPACING_6
        }
      }, {
        children: [heading && _jsx(Typography, Object.assign({
          text: heading,
          color: level == 'L3' && !isHeadingBold ? BLACK_700 : headingColor || typographyColor,
          type: showMultiLevelStepper ? getTypography(level, 'heading', isHeadingBold, isLargeScreen) : headingTypography,
          style: stepperStyles.heading
        }, getTestId(testID, 'heading'))), subHeading && (showSubheadingOnCollapsed ? !isExpanded : true) && _jsx(Typography, Object.assign({
          text: subHeading,
          color: subHeadingColor || BLACK_700,
          type: showMultiLevelStepper ? getTypography(level, 'subHeading', false, isLargeScreen) : 'body-para-sm',
          style: stepperStyles.subHeading
        }, getTestId(testID, 'subHeading')))]
      })), childElement && _jsx(View, Object.assign({
        style: stepperStyles.childrenContainer
      }, {
        children: childElement
      }))]
    }));
  };
  var MultiLevelStep = React.memo(_ref2 => {
    var step = _ref2.step,
      index = _ref2.index,
      stepperProps = _ref2.stepperProps,
      testID = _ref2.testID,
      isLastChild = _ref2.isLastChild;
    var _a;
    var _step$children = step.children,
      children = _step$children === void 0 ? [] : _step$children,
      level = step.level,
      _isExpanded = step.isExpanded;
    var _useState2 = useState(_isExpanded),
      isExpanded = _useState2[0],
      setIsExpanding = _useState2[1];
    var hasChildren = children.length > 0;
    var showIcon = (level === 'L1' || level === 'L2') && hasChildren;
    var showConnector = true;
    if (isLastChild && level !== 'L2') {
      if (hasChildren && isExpanded) {
        showConnector = true;
      } else {
        showConnector = false;
      }
    } else if (level === 'L2' && isLastItem(steps, index)) {
      showConnector = false;
    }
    return _jsxs(View, Object.assign({
      style: {
        flexDirection: 'row'
      }
    }, {
      children: [renderSingleStep(step, showConnector), _jsx(Collapsible, Object.assign({
        titleNoHorizontalPadding: true,
        childNoHorizontalPadding: true,
        childNoVerticalPadding: true,
        titleNoVerticalPadding: true,
        collapsibleWrapperStyles: {
          flex: 1
        },
        isExpanded: isExpanded,
        collapseContainerStyles: {
          marginLeft: level === 'L1' ? isLargeScreen ? -22 : -18 : 22
        },
        titleContainerStyles: {
          marginTop: level == 'L1' ? 2 : 0
        },
        titleComponent: renderStepContent(step, isExpanded),
        showIcon: showIcon,
        iconContainerStyles: {
          alignSelf: 'flex-start'
        }
      }, ((_a = steps[index]) === null || _a === void 0 ? void 0 : _a.children) && {
        onToggleHandler: isExpanded => {
          setIsExpanding(isExpanded);
          if (level === 'L1' && isExpanded && index === steps.length - 1) {
            if (titleComponentRef.current && titleComponentContainerRef.current) {
              titleComponentRef.current.measure((_x, _y, _width, height) => {
                setLastItemConnectorHeight(height);
              });
              titleComponentContainerRef.current.measure((_x, _y, _width, height) => {
                setLastItemConnectorHeight(h => h + height);
              });
            }
          }
        }
      }, {
        children: hasChildren && _jsx(View, {
          children: children.map((child, childIndex) => _jsx(MultiLevelStep, {
            step: child,
            childIndex: childIndex,
            stepperProps: stepperProps,
            index: index,
            testID: testID,
            isLastChild: childIndex === children.length - 1,
            isParentLastChild: isLastChild,
            isExpanded: isExpanded
          }, "child-" + childIndex))
        })
      }))]
    }), "multi-step-" + index);
  });
  if (showMultiLevelStepper) {
    return _jsx(View, Object.assign({
      ref: ref,
      style: [stepperStyles.stepperOuterContainer, style]
    }, restProps, getTestId(testID), {
      children: steps.map((step, index) => _jsx(MultiLevelStep, {
        step: step,
        index: index,
        stepperProps: stepperProps,
        isLastChild: index == steps.length - 1,
        testID: testID
      }, "step-" + index))
    }));
  }
  return _jsx(View, Object.assign({
    ref: ref,
    style: [stepperStyles.stepperOuterContainer, style]
  }, restProps, getTestId(testID), {
    children: steps === null || steps === void 0 ? void 0 : steps.map((step, index) => {
      var _a, _b, _c, _d, _e, _f, _g;
      var vertical = direction == 'vertical';
      var showLeftDivider = index === 0;
      var showRightDivider = index === steps.length - 1;
      var configProps = stepperProps === null || stepperProps === void 0 ? void 0 : stepperProps.config;
      var stepStates = (_b = (_a = configProps === null || configProps === void 0 ? void 0 : configProps[size]) === null || _a === void 0 ? void 0 : _a.state) === null || _b === void 0 ? void 0 : _b[step.state];
      var _connectorColor = (step === null || step === void 0 ? void 0 : step.connectorColor) || (stepStates === null || stepStates === void 0 ? void 0 : stepStates.connectorColor) || stepperProps.stepperConnectorColor;
      var previousStepRightConnectorColor = index > 0 ? ((_c = steps[index - 1]) === null || _c === void 0 ? void 0 : _c.connectorColor) || ((_g = (_e = (_d = configProps === null || configProps === void 0 ? void 0 : configProps[size]) === null || _d === void 0 ? void 0 : _d.state) === null || _e === void 0 ? void 0 : _e[(_f = steps[index - 1]) === null || _f === void 0 ? void 0 : _f.state]) === null || _g === void 0 ? void 0 : _g.connectorColor) || stepperProps.stepperConnectorColor : _connectorColor;
      return _jsxs(View, Object.assign({
        style: [stepperStyles.stepperContainer, {
          zIndex: (steps === null || steps === void 0 ? void 0 : steps.length) - index
        }]
      }, getTestId(testID, "stepper_item_" + index), {
        children: [_jsxs(View, Object.assign({
          style: stepperStyles.stepContainer
        }, {
          children: [!vertical && _jsx(View, Object.assign({
            style: [_connectorColor ? stepperStyles.multiLevelStepperConnector : stepperStyles.stepperConnecter, Object.assign({
              backgroundColor: index === 0 ? _connectorColor : previousStepRightConnectorColor
            }, showLeftDivider && stepperStyles.showLeftStepperConnecter)]
          }, getTestId(testID, 'vertical'))), renderSingleStep(step), _jsx(View, Object.assign({
            style: [_connectorColor ? stepperStyles.multiLevelStepperConnector : stepperStyles.stepperConnecter, Object.assign({
              backgroundColor: _connectorColor
            }, showRightDivider && stepperStyles.showRightStepperConnecter)]
          }, getTestId(testID, 'stepperConnector')))]
        })), renderStepContent(step)]
      }), "stepper_item_" + index);
    })
  }));
});